/// <amd-module name="Wit/DeliveryPlans/PlansTable/IPlansTableService" />
import { ArtifactScope, Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { getTFSPageData, IProjectData } from "Tfs/Platform/Page";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssPageService } from "VSS/Platform/Page";
import { ILinkProps } from "VSSUI/Link";
import { SortOrder } from "VSSUI/Table";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { PlanData } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import { IPlanService } from "../Common/Services/IPlanService";
import { getPlanUrl } from "../Common/Utils/RoutingUtils";

export namespace PlanFilterConstants {
    export const TextFilterKey = "TextFilter";
}

export interface PlansTableRowData extends PlanData {
    isFavoriteObservable: IObservableValue<boolean>;
    linkProps: ILinkProps;
}

export interface IPlansTableService extends IVssService {
    dataProviderErrorMessage: IObservableValue<string | undefined>;
    filter: Filter;
    rows: IObservableArray<PlansTableRowData>;
    deletePlan(id: string): Promise<void>;
    favoritePlan(id: string): Promise<void>;
    sortRows(
        columnIndex: number,
        sortOrder: SortOrder,
        sortFunction: (columnIndex: number, order: SortOrder, rows: PlansTableRowData[]) => PlansTableRowData[]
    ): void;
    unfavoritePlan(id: string): Promise<void>;
}

class PlansTableService extends VssService implements IPlansTableService {
    private _currentUser: IdentityRef | undefined;
    private _dataProviderErrorMessage: IObservableValue<string | undefined>;
    private _filter: Filter;
    private _planData: PlansTableRowData[];
    private _projectData: IProjectData | undefined;
    private _rows: IObservableArray<PlansTableRowData>;

    // fields required for sorting
    private _sort: (columnIndex: number, order: SortOrder, rows: PlansTableRowData[]) => PlansTableRowData[];
    private _sortColumnIndex: number;
    private _sortOrder: SortOrder;

    private _favoritesService: IFavoritesService;
    private _planService: IPlanService;

    public get dataProviderErrorMessage(): IObservableValue<string | undefined> {
        return this._dataProviderErrorMessage;
    }

    public get filter(): Filter {
        return this._filter;
    }

    public get rows(): IObservableArray<PlansTableRowData> {
        return this._rows;
    }

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData();
        this._currentUser = pageData?.user;
        this._dataProviderErrorMessage = new ObservableValue<string | undefined>(undefined);
        this._filter = new Filter();
        this._planData = [];
        this._projectData = getTFSPageData(pageContext)?.project;
        this._rows = new ObservableArray([]);

        this._sortColumnIndex = 1;
        this._sortOrder = SortOrder.ascending;

        this._favoritesService = pageContext.getService<IFavoritesService>("IFavoritesService");
        this._planService = pageContext.getService<IPlanService>("IPlanService");
        this._filter.subscribe(this._onFilterChanged);

        this._loadPlans();
    }

    public _serviceEnd() {
        this._filter.unsubscribe(this._onFilterChanged);
    }

    public async deletePlan(id: string): Promise<void> {
        const index = this._rows.value.findIndex(p => p.id === id);
        const plansListIndex = this._planData.findIndex(p => p.id === id);
        if (index >= 0 && plansListIndex >= 0) {
            const rowData = this._rows.value[index];
            try {
                // optimistically remove row
                this._rows.splice(index, 1);
                this._planData.splice(plansListIndex, 1);

                const promises = [this._planService.deletePlan(id)];
                if (rowData.isFavoriteObservable.value) {
                    promises.push(this.unfavoritePlan(rowData.id));
                }

                await Promise.all(promises);
            } catch (ex) {
                // set the error and add the row back
                this._dataProviderErrorMessage.value = ex?.message;
                this._rows.splice(index, 0, rowData);
                this._planData.splice(plansListIndex, 1);
            }
        }
    }

    public async favoritePlan(id: string): Promise<void> {
        const rowData = this._rows.value.find(p => p.id === id);
        if (this._projectData && this._currentUser && rowData) {
            rowData.isFavoriteObservable.value = true;
            const scope: ArtifactScope = { id: this._projectData.id, name: this._projectData.name, type: "Project" };

            const newFavorite = {
                artifactId: rowData.id,
                artifactName: rowData.name,
                artifactScope: scope,
                artifactType: FavoriteTypes.WORK_PLAN,
                owner: this._currentUser
            } as Favorite;

            try {
                await this._favoritesService.addFavorite(newFavorite);
            } catch (ex) {
                // set the error and favorite value back
                this._dataProviderErrorMessage.value = ex?.message;
                rowData.isFavoriteObservable.value = false;
            }
        }
    }

    public sortRows(
        columnIndex: number,
        sortOrder: SortOrder,
        sortFunction: (columnIndex: number, order: SortOrder, rows: PlansTableRowData[]) => PlansTableRowData[]
    ): void {
        // save off the sort data because we will need to re-use on filtering
        this._sortColumnIndex = columnIndex;
        this._sortOrder = sortOrder;
        this._sort = sortFunction;

        this._updateDisplayedRows();
    }

    public async unfavoritePlan(id: string): Promise<void> {
        const rowData = this._rows.value.find(p => p.id === id);
        if (this._projectData && this._currentUser && rowData) {
            rowData.isFavoriteObservable.value = false;

            const scope: ArtifactScope = { id: this._projectData.id, name: this._projectData.name, type: "Project" };
            const favorites = await this._favoritesService.getFavorites(FavoriteTypes.WORK_PLAN, scope.type, scope.id, false);

            // this *should* be a single favorite
            const matchedFavorites = favorites.value.filter(favorite => favorite.artifactId === rowData.id);
            for (const favorite of matchedFavorites) {
                try {
                    await this._favoritesService.removeFavorite(favorite);
                } catch (ex) {
                    // set the error and favorite value back
                    this._dataProviderErrorMessage.value = ex?.message;
                    rowData.isFavoriteObservable.value = true;
                }
            }
        }
    }

    private _loadPlans() {
        const plans = this._planService.getPlans();
        if (plans) {
            this._planData = this._getPlansTableRowData(plans);
            this._rows.value = [...this._planData];
        } else {
            this.dataProviderErrorMessage.value = this._planService.getPlansErrorMessage();
        }
    }

    private _getPlansTableRowData(plans: PlanData[]): PlansTableRowData[] {
        return plans.map(p => {
            return {
                isFavoriteObservable: new ObservableValue(p.isFavorite),
                linkProps: {
                    href: getPlanUrl(this.pageContext, p.id)
                },
                ...p
            };
        });
    }

    private _updateDisplayedRows() {
        const filteredPlans = this._applyFilter(this._filter.getState());
        const sortedPlans = this._sort ? this._sort(this._sortColumnIndex, this._sortOrder, filteredPlans) : filteredPlans;
        this._rows.value = [...sortedPlans];
    }

    private _onFilterChanged = (filterState: IFilterState, action: string) => {
        if (action === "filter-applied") {
            this._updateDisplayedRows();
        }
    };

    private _applyFilter(filterState: IFilterState): PlansTableRowData[] {
        const property = filterState[PlanFilterConstants.TextFilterKey];
        return this._filterPlans(property ? property.value : "");
    }

    private _filterPlans(filterText: string): PlansTableRowData[] {
        if (filterText === "") {
            return this._planData;
        } else {
            const filterTextToUpper = filterText.toLocaleUpperCase();
            return this._planData.filter(p => this._matchFilterText(p, filterTextToUpper));
        }
    }

    private _matchFilterText(planData: PlanData, filterText: string): boolean {
        return (
            planData.name.toLocaleUpperCase().includes(filterText) ||
            planData.description?.toLocaleUpperCase().includes(filterText) ||
            planData.createdByIdentity?.displayName?.toLocaleUpperCase().includes(filterText)
        );
    }
}

const serviceName = "IPlansTableService";
Services.add(serviceName, { serviceFactory: PlansTableService });
